/**
 * Create Subtask Button
 *
 * Opens a simplified dialog to create a subtask under a parent task.
 * Inherits repo and sets trigger to 'immediate' by default.
 */

import React from 'react';
import { Dialog, DialogTrigger, DialogContent, DialogFooter, Button, TextField, TextArea, Select, Text, useToast } from '@chatsdet/ui';
import { Plus } from '@phosphor-icons/react';
import { useCreateTask } from '../hooks/useTasks';
import type { AgentTask, AgentTaskTriggerType } from '../types';

const PRIORITY_OPTIONS = [
  { id: '0', label: 'Normal' },
  { id: '1', label: 'High' },
  { id: '2', label: 'Critical' },
];

interface CreateSubtaskButtonProps {
  parentTask: AgentTask;
}

export function CreateSubtaskButton({ parentTask }: CreateSubtaskButtonProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [priority, setPriority] = React.useState<string>('0');

  const createTask = useCreateTask();
  const { toast } = useToast();

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setPriority('0');
  };

  const handleSubmit = () => {
    if (!title.trim() || !description.trim()) return;

    createTask.mutate(
      {
        propertyGithubRepoId: parentTask.propertyGithubRepoId,
        title: title.trim(),
        description: description.trim(),
        triggerType: 'immediate' as AgentTaskTriggerType,
        priority: parseInt(priority, 10),
        parentTaskId: parentTask.id,
      },
      {
        onSuccess: () => {
          toast.success('Subtask created');
          resetForm();
          setIsOpen(false);
        },
        onError: () => {
          toast.error('Failed to create subtask');
        },
      }
    );
  };

  const canSubmit = !!title.trim() && !!description.trim() && !createTask.isPending;

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen} size="lg">
      <Button
        variant="ghost"
        size="sm"
        className="text-muted-foreground hover:text-foreground"
        iconLeft={<Plus weight="bold" size={13} />}
      >
        Add subtask
      </Button>
      <Dialog
        title="Create Subtask"
        description={`Subtask of: ${parentTask.title}`}
      >
        <DialogContent>
          <Text size="sm" className="text-muted-foreground">
            Parent: {parentTask.title}
          </Text>
          <TextField
            label="Title"
            value={title}
            onChange={setTitle}
            placeholder="Brief description of the subtask"
          />
          <TextArea
            label="Description"
            value={description}
            onChange={setDescription}
            placeholder="What needs to be done"
            className="min-h-[100px]"
          />
          <Select
            label="Priority"
            items={PRIORITY_OPTIONS}
            selectedKey={priority}
            onSelectionChange={(key) => setPriority(key as string)}
          />
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
          >
            {createTask.isPending ? 'Creating...' : 'Create Subtask'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
